import {
  applicantSection,
  businessSection,
  deMinimisAttachments,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { bankAccountField } from "modules/ebiForms/definitions/sharedEducationSafety";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * D6. Wniosek pracodawcy o dofinansowanie kosztów kształcenia młodocianego pracownika — art. 122 ustawy — Prawo
 * oświatowe (ust. 7 — termin 3 miesięcy od egzaminu, ust. 11–13 — załączniki, ust. 16 — pomoc de minimis). Brak wzoru
 * ogólnopolskiego wniosku (formularz informacji de minimis jest ogólnopolski — UOKiK); układ jak Druk nr 1 Białegostoku
 * (https://bip.bialystok.pl/resource/79410/Druk+Nr+1+Wniosek+o+dofinansowanie+kosztow+m%C5%82odocianego+pracownika.doc
 * — pobrany 2026-10-22, tekst odczytany częściowo): pracodawca (REGON, NIP), oświadczenie o kwalifikacjach i ukończeniu
 * nauki, okres umowy, dane młodocianego, rachunek, lista załączników. Rozgałęzienie „zdał / przystąpił do egzaminu”
 * steruje załącznikami (ust. 12 i 13). Wnioskodawcą jest pracodawca (sekcja przedsiębiorcy); zawiadomienie o zawarciu
 * umowy z młodocianym to osobna sprawa.
 */
const PASSED: EbiCondition = { field: "egzamin", equals: "zdal" };
const ATTEMPTED: EbiCondition = { field: "egzamin", equals: "przystapil" };

const D6: IEbiFormDefinition = {
  code: "D6",
  version: 1,
  title: "Wniosek o dofinansowanie kosztów kształcenia młodocianego pracownika",
  category: EEbiFormCategory.Education,
  office: EEbiFormOffice.MunicipalUnit,
  description:
    "Dofinansowanie dla pracodawcy, który przygotował zawodowo młodocianego pracownika — wniosek w terminie 3 miesięcy od egzaminu.",
  keywords: ["młodociany pracownik", "dofinansowanie", "nauka zawodu", "przyuczenie", "pracodawca", "de minimis", "egzamin czeladniczy"],
  organ: "Wójt / Burmistrz / Prezydent Miasta właściwy ze względu na miejsce zamieszkania młodocianego pracownika",
  legalBasis: "art. 122 ustawy z dnia 14 grudnia 2016 r. — Prawo oświatowe",
  request:
    "Na podstawie art. 122 ustawy z dnia 14 grudnia 2016 r. — Prawo oświatowe wnoszę o dofinansowanie kosztów kształcenia młodocianego pracownika wskazanego we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do miesiąca",
  deadline: "Do miesiąca, w sprawach szczególnie skomplikowanych do 2 miesięcy (art. 35 KPA).",
  sections: [
    applicantSection("Osoba składająca wniosek w imieniu pracodawcy"),
    {
      ...businessSection("Pracodawca (wnioskodawca)"),
      description:
        "Wnioskodawcą jest pracodawca młodocianego. Jeśli działasz jako jego pełnomocnik, w kolejnej części wskaż pracodawcę jako mocodawcę i dołącz pełnomocnictwo.",
    },
    representationSection(),
    {
      code: "mlodociany",
      title: "Dane młodocianego pracownika",
      fields: [
        { code: "mlodocianyImie", type: "text", label: "Imię", required: true, width: "half", maxLength: 100 },
        { code: "mlodocianyNazwisko", type: "text", label: "Nazwisko", required: true, width: "half", maxLength: 100 },
        { code: "mlodocianyAdres", type: "text", label: "Adres zamieszkania — ulica, numer domu i lokalu", required: true, maxLength: 200 },
        { code: "mlodocianyKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, width: "third", placeholder: "00-000" },
        { code: "mlodocianyMiejscowosc", type: "text", label: "Miejscowość", required: true, width: "twoThirds", maxLength: 100 },
      ],
    },
    {
      code: "przygotowanie",
      title: "Przygotowanie zawodowe",
      fields: [
        {
          code: "formaPrzygotowania",
          type: "radio",
          label: "Forma przygotowania zawodowego",
          required: true,
          options: [
            { value: "naukaZawodu", label: "nauka zawodu" },
            { value: "przyuczenie", label: "przyuczenie do wykonywania określonej pracy" },
          ],
        },
        { code: "zawod", type: "text", label: "Zawód albo rodzaj pracy, do której przyuczano", required: true, maxLength: 200 },
        { code: "umowaOd", type: "date", label: "Umowa o pracę w celu przygotowania zawodowego zawarta na okres od", required: true, width: "half" },
        { code: "umowaDo", type: "date", label: "do", required: true, width: "half" },
        {
          code: "okresUwagi",
          type: "textarea",
          label: "Uwagi o okresie kształcenia",
          hint: "Np. wcześniejsze rozwiązanie umowy, kształcenie u poprzedniego pracodawcy, przerwy w kształceniu.",
          maxLength: 1000,
        },
        {
          code: "kwalifikacje",
          type: "radio",
          label: "Kwalifikacje wymagane do prowadzenia przygotowania zawodowego młodocianych posiada",
          required: true,
          options: [
            { value: "pracodawca", label: "pracodawca" },
            { value: "prowadzacy", label: "osoba prowadząca zakład w imieniu pracodawcy" },
            { value: "zatrudniony", label: "osoba zatrudniona u pracodawcy (instruktor praktycznej nauki zawodu)" },
          ],
        },
        {
          code: "kwalifikacjeOsoba",
          type: "text",
          label: "Imię i nazwisko osoby posiadającej kwalifikacje",
          required: true,
          visibleWhen: { field: "kwalifikacje", in: ["prowadzacy", "zatrudniony"] },
          maxLength: 200,
        },
      ],
    },
    {
      code: "wynikEgzaminu",
      title: "Egzamin",
      fields: [
        {
          code: "egzamin",
          type: "radio",
          label: "Młodociany pracownik",
          required: true,
          options: [
            { value: "zdal", label: "ukończył naukę zawodu albo przyuczenie i zdał egzamin" },
            { value: "przystapil", label: "przystąpił do egzaminu — dołączam dokumenty, o których mowa w art. 122 ust. 13" },
          ],
        },
        {
          code: "dataEgzaminu",
          type: "date",
          label: "Data zdania egzaminu albo przystąpienia do niego",
          hint: "Wniosek składa się w terminie 3 miesięcy od tej daty (art. 122 ust. 7).",
          required: true,
          width: "half",
        },
        feeInfo(
          "infoKwota",
          "Kwota dofinansowania zależy od formy przygotowania zawodowego i okresu kształcenia (art. 122 ust. 2–3 ustawy — Prawo oświatowe); aktualne kwoty ogłasza minister właściwy do spraw oświaty. Dofinansowanie jest pomocą de minimis.",
        ),
      ],
    },
    {
      code: "wyplata",
      title: "Wypłata dofinansowania",
      fields: [
        bankAccountField("rachunek", "Numer rachunku bankowego, na który należy przekazać dofinansowanie", { required: true }),
        { code: "bank", type: "text", label: "Nazwa banku", width: "half", maxLength: 200 },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        {
          code: "oswiadczenieWarunki",
          type: "declaration",
          label:
            "Oświadczam, że spełniam warunki ubiegania się o dofinansowanie kosztów kształcenia młodocianego pracownika określone w art. 122 ust. 1 ustawy — Prawo oświatowe, a dane podane we wniosku są zgodne ze stanem faktycznym.",
          required: true,
        },
      ],
    },
    replySection("Sposób odbioru decyzji"),
  ],
  attachments: [
    {
      code: "kwalifikacjeDokumenty",
      label: "Kopie dokumentów potwierdzających kwalifikacje do prowadzenia przygotowania zawodowego młodocianych",
      hint: "Pracodawcy, osoby prowadzącej zakład albo instruktora, np. dyplom mistrzowski, zaświadczenie o ukończeniu kursu pedagogicznego (art. 122 ust. 11 pkt 1).",
      required: true,
      multiple: true,
    },
    {
      code: "umowaOPrace",
      label: "Kopia umowy o pracę z młodocianym pracownikiem zawartej w celu przygotowania zawodowego",
      hint: "Art. 122 ust. 11 pkt 2.",
      required: true,
    },
    ...deMinimisAttachments(),
    {
      code: "dyplom",
      label: "Kopia dyplomu, certyfikatu, świadectwa albo zaświadczenie potwierdzające zdanie egzaminu",
      hint: "Art. 122 ust. 12.",
      visibleWhen: PASSED,
      required: true,
    },
    {
      code: "swiadectwoPracy",
      label: "Kopia świadectwa pracy albo zaświadczenie o okresie zatrudnienia młodocianego pracownika",
      hint: "Art. 122 ust. 13.",
      visibleWhen: ATTEMPTED,
      required: true,
    },
    {
      code: "przystapienie",
      label:
        "Świadectwo ukończenia branżowej szkoły I stopnia albo zaświadczenie izby rzemieślniczej, dyrektora szkoły lub okręgowej komisji egzaminacyjnej o przystąpieniu do egzaminu",
      hint: "Art. 122 ust. 13.",
      visibleWhen: ATTEMPTED,
      required: true,
    },
    {
      code: "wpisRejestr",
      label: "Wydruk z CEIDG albo KRS (przy spółce cywilnej — także umowa spółki)",
      hint: "Część urzędów o to prosi (nieobowiązkowo).",
    },
    {
      code: "oswiadczenieKoszty",
      label: "Oświadczenie o poniesionych kosztach kształcenia młodocianego",
      hint: "Część urzędów o to prosi (nieobowiązkowo).",
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default D6;
